import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CloseIcon,
  FlexControlledForm,
  InfoOutlineIcon,
  Inscription,
  Text,
} from '@common-fe/common-fe';

import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';

import { ADD_NEW_AGGREGATE_TRIGGER_KEY, ADD_NEW_EMBEDDED_TRIGGER_KEY } from './hooks/useTierFields';
import useTierFields from './hooks/useTierFields';
import { TierField } from './hooks/useTiers';
import { CARD_PURSE_LOCK, EMBEDDED_ONLY } from './ProcessingRuleModal.constants';
import { getProcessingRuleTierElementId } from './ProcessingRuleModal.utils';

interface Props {
  isRemoveMode?: boolean;
  onRemove: (id: string) => void;
  isLastItem?: boolean;
  id?: string;
  index: number;
  defaultValues?: Partial<TierField>;
  isErrorShowed?: boolean;
  onChangeValues: (value: TierField) => void;
  handleClearValues?: (id: string) => void;
  tiersValues?: TierField[];
  setShouldConfirmOnClose?: (value: boolean) => void;
  processingRuleType?: string;
  onAddNewAggregateTrigger?: () => void;
  onAddNewEmbeddedTrigger?: () => void;
  isHealthPlanProcessingRule?: boolean;
  isTemplateMode?: boolean;
}

const TierItem: React.FC<Props> = ({
  isRemoveMode,
  onRemove,
  id = '',
  index,
  isErrorShowed,
  onChangeValues,
  defaultValues,
  isLastItem,
  handleClearValues,
  tiersValues,
  setShouldConfirmOnClose,
  processingRuleType,
  onAddNewAggregateTrigger,
  onAddNewEmbeddedTrigger,
  isHealthPlanProcessingRule,
  isTemplateMode,
}) => {
  const isHidePurseLockEnabled = useFeatureFlag(FeatureFlags.HIDE_PURSE_LOCK);

  const showLockCardPurseInfoPanel = useMemo(() => {
    if (!isLastItem || !isHealthPlanProcessingRule || isTemplateMode || isHidePurseLockEnabled) {
      return false;
    }
    if (processingRuleType === EMBEDDED_ONLY) {
      return false;
    }
    return (tiersValues || []).some((tier) => tier.lockCardPurse === CARD_PURSE_LOCK);
  }, [
    isLastItem,
    isHealthPlanProcessingRule,
    isTemplateMode,
    isHidePurseLockEnabled,
    tiersValues,
    processingRuleType,
  ]);

  const handleFormChangeValues = useCallback(
    (values: TierField) => {
      if (values?.aggregateTrigger === ADD_NEW_AGGREGATE_TRIGGER_KEY) {
        onAddNewAggregateTrigger?.();
        return;
      }
      if (values?.embeddedTrigger === ADD_NEW_EMBEDDED_TRIGGER_KEY) {
        onAddNewEmbeddedTrigger?.();
        return;
      }
      onChangeValues(values);
    },
    [onChangeValues, onAddNewAggregateTrigger, onAddNewEmbeddedTrigger]
  );

  const fields = useTierFields({
    tiersValues,
    defaultValues,
    isLastItem,
    handleClearValues,
    processingRuleType,
    isHealthPlanProcessingRule,
    isTemplateMode,
  });

  return (
    <Box
      id={getProcessingRuleTierElementId(id)}
      round="container1Round"
      background="canvas"
      border={{ size: 'small', color: 'border2' }}
      elevation="default"
      margin={{ bottom: 'spacing24' }}
      pad={{ vertical: 'spacing24' }}
    >
      <Box
        direction="row"
        align="center"
        justify="between"
        margin={{ horizontal: 'spacing24', bottom: 'spacing16' }}
      >
        <Text size="xxlarge" weight={500}>{`Tier ${index + 1}`}</Text>
        {isRemoveMode && (
          <Button data-testid={`tier-item_${id}_remove-button`} onClick={() => onRemove(id)}>
            <Box direction="row" align="center">
              <Text color="danger" size="medium" weight="normal" margin={{ right: 'spacing4' }}>
                Remove
              </Text>
              <CloseIcon size="16px" color="danger" />
            </Box>
          </Button>
        )}
      </Box>
      <FlexControlledForm
        key={id}
        isModalType
        testId={`tier-item_${id}`}
        fields={fields}
        editMode
        onDirty={setShouldConfirmOnClose}
        onChangeValues={handleFormChangeValues}
        showError={isErrorShowed}
        wrapperStyle={{ border: 'none' }}
        formStyle={{ marginBottom: 0, marginTop: 0 }}
      />
      {showLockCardPurseInfoPanel && (
        <Box
          data-testid="lock-card-purse-info-panel"
          direction="row"
          align="center"
          gap="spacing8"
          background="infoContainer"
          border={{ color: 'infoBorder' }}
          round="container2Round"
          pad={{ horizontal: 'spacing16', vertical: 'spacing8' }}
          margin={{ horizontal: 'spacing24', top: 'spacing16' }}
        >
          <InfoOutlineIcon color="info" size="24px" />
          <Inscription>In last tier all card purses will be automatically unlocked</Inscription>
        </Box>
      )}
    </Box>
  );
};

export default TierItem;
